import { Component } from '@theme/component'
import { useIntersection } from '@theme/use-intersection'

const AnnouncementBarConfig = {
  attributes: {
    ariaHidden: 'aria-hidden',
    loaded: 'loaded'
  },
  autoplay: {
    defaultDelay: 5000
  },
  classes: {
    active: 'is-active',
    leaving: 'is-leaving'
  },
  events: {
    change: 'slider:change',
    shopifySelect: 'shopify:block:select'
  },
  intersection: {
    rootMargin: '200px 0px 200px 0px'
  },
  selectors: {
    nextButton: 'button[is="next-button"]',
    prevButton: 'button[is="previous-button"]',
    slide: '.announcement__slide'
  },
  transition: {
    leavingMs: 600
  }
}

const root = globalThis
const theme = root.theme || {}
root.theme = theme
theme.config = theme.config || {}
theme.InitWhenVisible ??= class {
  constructor(callback) {
    if (typeof callback === 'function') {
      callback()
    }
  }
}

class AnnouncementBar extends Component {
  constructor() {
    super()
    this.currentIndex = 0
    this.isPlaying = false
    this.interval = null
    this.leavingTimers = new Map()
    this.nextButton = null
    this.prevButton = null
    this.unobserve = null
  }

  connectedCallback() {
    super.connectedCallback()

    if (theme.config.isTouch) {
      new theme.InitWhenVisible(this.init)
      return
    }

    const intersection = useIntersection(AnnouncementBarConfig.intersection)
    this.unobserve = intersection.observe(this, (entry, obs) => {
      if (!entry.isIntersecting) return

      this.init()
      obs.unobserve(this)
    })

    if (!intersection.size) {
      this.init()
    }
  }

  get items() {
    this._items =
      this._items ||
      Array.from(this.querySelectorAll(AnnouncementBarConfig.selectors.slide))
    return this._items
  }

  get autoplayDelay() {
    const delay = Number.parseInt(this.getAttribute('data-delay'), 10)
    if (!Number.isFinite(delay) || delay <= 0) {
      return AnnouncementBarConfig.autoplay.defaultDelay
    }

    return delay
  }

  get shouldAutoplay() {
    const autoplay = this.getAttribute('data-autoplay')
    return autoplay === 'true'
  }

  init = () => {
    if (this.initialized) return
    this.initialized = true

    if (this.items.length > 1) {
      this.updateSlides()
      this.setAttribute(AnnouncementBarConfig.attributes.loaded, '')

      this.prevButton = this.parentElement.querySelector(
        AnnouncementBarConfig.selectors.prevButton
      )
      this.nextButton = this.parentElement.querySelector(
        AnnouncementBarConfig.selectors.nextButton
      )

      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.handlePreviousClick)
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.handleNextClick)
      }

      this.isPlaying = this.shouldAutoplay

      if (this.isPlaying) {
        this.startAutoplay()
      }

      if (Shopify.designMode) {
        this.addEventListener(
          AnnouncementBarConfig.events.shopifySelect,
          this.handleShopifySelect
        )
      }
      return
    }

    this.setAttribute(AnnouncementBarConfig.attributes.loaded, '')
    this.items[0]?.classList.add(AnnouncementBarConfig.classes.active)
  }

  updateSlides() {
    for (const [index, item] of this.items.entries()) {
      if (index === this.currentIndex) {
        this.activateSlide(item)
        continue
      }

      this.deactivateSlide(item)
    }

    this.dispatchEvent(
      new CustomEvent(AnnouncementBarConfig.events.change, {
        bubbles: true,
        detail: { currentPage: this.currentIndex }
      })
    )
  }

  activateSlide(item) {
    this.clearLeavingTimer(item)
    item.classList.remove(AnnouncementBarConfig.classes.leaving)
    item.classList.add(AnnouncementBarConfig.classes.active)
    item.removeAttribute(AnnouncementBarConfig.attributes.ariaHidden)
  }

  deactivateSlide(item) {
    const wasActive = item.classList.contains(
      AnnouncementBarConfig.classes.active
    )
    item.classList.remove(AnnouncementBarConfig.classes.active)
    item.setAttribute(AnnouncementBarConfig.attributes.ariaHidden, 'true')

    if (wasActive) {
      this.markSlideAsLeaving(item)
    }
  }

  markSlideAsLeaving(item) {
    this.clearLeavingTimer(item)
    item.classList.add(AnnouncementBarConfig.classes.leaving)

    const timer = window.setTimeout(() => {
      item.classList.remove(AnnouncementBarConfig.classes.leaving)
      this.leavingTimers.delete(item)
    }, AnnouncementBarConfig.transition.leavingMs)

    this.leavingTimers.set(item, timer)
  }

  clearLeavingTimer(item) {
    const timer = this.leavingTimers.get(item)
    if (!timer) return

    window.clearTimeout(timer)
    this.leavingTimers.delete(item)
  }

  clearLeavingTimers() {
    for (const timer of this.leavingTimers.values()) {
      window.clearTimeout(timer)
    }

    this.leavingTimers.clear()
  }

  handleNextClick = () => {
    this.next()
    this.resetAutoplay()
  }

  handlePreviousClick = () => {
    this.previous()
    this.resetAutoplay()
  }

  handleShopifySelect = (event) => {
    const index = this.items.indexOf(event.target)
    if (index !== -1) {
      this.select(index)
    }
  }

  next = () => {
    this.currentIndex = (this.currentIndex + 1) % this.items.length
    this.updateSlides()
  }

  previous = () => {
    this.currentIndex =
      (this.currentIndex - 1 + this.items.length) % this.items.length
    this.updateSlides()
  }

  select = (index) => {
    this.currentIndex = index
    this.updateSlides()
  }

  startAutoplay = () => {
    this.stopAutoplay()
    this.interval = setInterval(() => this.next(), this.autoplayDelay)
  }

  stopAutoplay = () => {
    if (this.interval) {
      clearInterval(this.interval)
      this.interval = null
    }
  }

  resetAutoplay = () => {
    if (!this.isPlaying) {
      return
    }

    this.startAutoplay()
  }

  disconnectedCallback() {
    this.stopAutoplay()
    this.clearLeavingTimers()
    this.unobserve?.()
    this.prevButton?.removeEventListener('click', this.handlePreviousClick)
    this.nextButton?.removeEventListener('click', this.handleNextClick)
    this.removeEventListener(
      AnnouncementBarConfig.events.shopifySelect,
      this.handleShopifySelect
    )
    super.disconnectedCallback()
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', AnnouncementBar)
}
